import { useState, useCallback } from 'react';
import type { TokenInfo } from '../api/bipApi';

const GMGN_BASE = 'https://gmgn.ai/sol/token/';

interface TokenInfoSectionProps {
  token: TokenInfo;
  animationDelay?: string;
}

function formatPoolDate(iso?: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function TokenInfoRow({ label, value, href }: { label: string; value: React.ReactNode; href?: string }) {
  if (value == null || value === '' || value === '—') return null;
  const content = href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className="token-info-value token-info-link">
      {String(value)}
    </a>
  ) : (
    <span className="token-info-value">{value}</span>
  );
  return (
    <div className="token-info-row">
      <span className="token-info-label">{label}</span>
      {content}
    </div>
  );
}

export default function TokenInfoSection({ token, animationDelay = '0s' }: TokenInfoSectionProps) {
  const displaySymbol = token.symbol ?? '—';
  const displayImage = token.imageUrl ?? null;
  const [copied, setCopied] = useState(false);

  const copyAddress = useCallback(() => {
    if (!token.address) return;
    navigator.clipboard.writeText(token.address).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [token.address]);

  const gmgnUrl = token.address ? `${GMGN_BASE}${token.address}` : null;

  return (
    <section className="result-section token-info-section fade-in" style={{ animationDelay }}>
      <div className="token-info-card">
        <div className="token-info-header">
          {displayImage && (
            <img src={displayImage} alt={displaySymbol} className="token-info-image" />
          )}
          <div className="token-info-header-text">
            <h3 className="token-info-symbol">
              {displaySymbol === '—' ? <span className="value-empty">—</span> : displaySymbol}
            </h3>
          </div>
        </div>
        <div className="token-info-grid">
          {token.address ? (
            <div className="token-info-row">
              <span className="token-info-label">Address</span>
              <div className="token-info-address-wrap">
                <span className="token-info-value">{token.address}</span>
                <div className="token-info-address-actions">
                  <button
                    type="button"
                    className={`token-info-address-btn ${copied ? 'token-info-address-btn--copied' : ''}`}
                    onClick={copyAddress}
                    title="Copy address"
                    aria-label="Copy address"
                  >
                    <svg className="token-info-address-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                    </svg>
                  </button>
                  {gmgnUrl && (
                    <a
                      href={gmgnUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="token-info-address-btn token-info-address-btn--link"
                      title="Open GMGN chart"
                      aria-label="Open GMGN chart"
                    >
                      <img src="/static/gmgn.svg" alt="GMGN" className="token-info-address-gmgn" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ) : null}
          <TokenInfoRow label="Symbol" value={token.symbol} />
          <TokenInfoRow label="Decimals" value={token.decimals != null ? String(token.decimals) : undefined} />
          <TokenInfoRow label="Total supply" value={token.totalSupply ? (Number(token.totalSupply) / 10 ** (token.decimals ?? 1)).toLocaleString() : undefined} />
          <TokenInfoRow label="Website" value={token.website} href={token.website} />
          <TokenInfoRow label="Created on" value={token.createdOn} href={token.createdOn} />
          <TokenInfoRow label="Pool address" value={token.poolAddress} />
          <TokenInfoRow label="Pool created" value={formatPoolDate(token.poolCreatedAt)} />
        </div>
      </div>
    </section>
  );
}
